import { A11yModule } from '@angular/cdk/a11y';
import {
    afterNextRender,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    forwardRef,
    HostListener,
    inject,
    Injector,
    input,
    OnInit,
    signal,
    untracked,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SI_FORM_ITEM_CONTROL, SiFormItemComponent, SiFormItemControl } from '@siemens/element-ng/form';
import { tap } from 'rxjs';
import { CustomControl } from '../custom-control/custom-control.component';
import { CustomValueSelectItem } from './custom-value-select-item.type';
import { CustomValueSelectDirective } from './directives/custom-value-select.directive';

@Component({
    selector: 'tran-custom-value-select-field',
    imports: [ReactiveFormsModule, TranslateModule, A11yModule, CustomValueSelectDirective, SiFormItemComponent],
    templateUrl: './custom-value-select-field.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomValueSelectFieldComponent),
            multi: true
        },
        {
            provide: SI_FORM_ITEM_CONTROL,
            useExisting: forwardRef(() => CustomValueSelectFieldComponent)
        }
    ]
})
export class CustomValueSelectFieldComponent<T extends object = Record<string, unknown>>
    extends CustomControl<CustomValueSelectItem<T>[]>
    implements OnInit, AfterViewInit, SiFormItemControl
{
    private static idCounter = 0;

    readonly id = `__tran-custom-value-select-${CustomValueSelectFieldComponent.idCounter++}`;
    readonly errormessageId = `${this.id}-errormessage`;

    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly hostElement = inject(ElementRef<HTMLElement>);

    public readonly options = input<T[]>([]);
    public readonly identifierProperty = input<keyof T>('id' as keyof T);
    public readonly displayProperty = input<keyof T | string>('name');
    public readonly displayTranslationPrefix = input<string | null>(null);
    public readonly label = input<string>('');
    public readonly placeholder = input<string>('components.custom-value-multi-select.select-values');
    public readonly required = input(false);
    public readonly maxSelected = input<number | string | null>(null);
    public readonly readonly = input(false);
    public readonly errorMapper = input<Record<string, string>>({});

    private readonly triggerButton = viewChild.required<ElementRef<HTMLButtonElement>>('triggerButton');
    private readonly namesContainerRef = viewChild<ElementRef<HTMLElement>>('namesContainer');
    private readonly overflowBadgeRef = viewChild<ElementRef<HTMLElement>>('overflowBadge');

    protected readonly selectedIds = signal<Set<string>>(new Set());
    protected readonly selectedCustomValues = signal<string[]>([]);
    protected readonly open = signal(false);
    protected readonly overflowCount = signal(0);
    protected readonly uncheckedCustomValues = signal<string[]>([]);
    protected readonly isInvalid = signal(false);

    protected readonly maxSelectionReached = computed(() => {
        const max = this.normalizedMaxSelected();
        return max !== null && this.selectedCount() >= max;
    });
    protected readonly singleSelection = computed(() => this.normalizedMaxSelected() === 1);

    protected readonly allCustomItems = computed(() => [
        ...this.selectedCustomValues().map(value => ({ value, checked: true })),
        ...this.uncheckedCustomValues().map(v => ({ value: v, checked: false }))
    ]);

    protected readonly predefinedOptions = computed(() =>
        this.options().map(option => {
            const id = String(option[this.identifierProperty()]);
            return {
                option,
                id,
                label: String(option[this.displayProperty() as keyof T]),
                selected: this.selectedIds().has(id),
                disabled:
                    this.control.disabled ||
                    this.readonly() ||
                    (!this.singleSelection() && this.maxSelectionReached() && !this.selectedIds().has(id))
            };
        })
    );

    protected readonly selectedDisplayItems = computed(() => {
        const prefix = this.displayTranslationPrefix();
        const predefined = this.predefinedOptions()
            .filter(item => item.selected)
            .map(item => ({ translationKey: prefix ? prefix + item.label : item.label, isCustom: false }));
        const custom = this.selectedCustomValues().map(name => ({ translationKey: name, isCustom: true }));
        return [...predefined, ...custom];
    });

    public constructor() {
        super();

        effect(() => {
            this.selectedDisplayItems();
            untracked(() => this.scheduleOverflowUpdate());
        });
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        this.updateInvalidState();

        toSignal(this.control.events.pipe(tap(() => this.updateInvalidState())), { injector: this.injector });

        this.listenToControlValueChanges();
    }

    public ngAfterViewInit(): void {
        const observer = new ResizeObserver(() => this.scheduleOverflowUpdate());
        observer.observe(this.triggerButton().nativeElement);
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    protected togglePredefined(option: T): void {
        const optionId = String(option[this.identifierProperty()]);
        const isSelected = this.selectedIds().has(optionId);
        if (!isSelected && this.singleSelection()) {
            this.setSelection([{ type: 'predefined', value: option }]);
            this.closePanel();
            return;
        }
        if (!isSelected && !this.canSelectMore()) return;
        const selection = this.control.value ?? [];
        const updatedSelection = isSelected
            ? selection.filter(
                  item => item.type !== 'predefined' || String(item.value[this.identifierProperty()]) !== optionId
              )
            : [...selection, { type: 'predefined' as const, value: option }];
        this.setSelection(updatedSelection);
        if (this.singleSelection()) this.closePanel();
    }

    protected closePanel(): void {
        this.open.set(false);
        this.triggerButton().nativeElement.focus();
    }

    protected openPanel(): void {
        const isOpening = !this.open();
        this.open.set(isOpening);
        if (isOpening) {
            afterNextRender(
                () => {
                    const first = document.querySelector<HTMLElement>('[data-option-btn]:not([disabled])');
                    first?.focus();
                },
                { injector: this.injector }
            );
        }
    }

    protected addCustom(value: string): void {
        const trimmed = value.trim();
        if (!trimmed) return;
        if (this.control.disabled) return;
        if (!this.singleSelection() && !this.canSelectMore()) return;
        if (this.singleSelection()) {
            this.setSelection([{ type: 'custom', value: trimmed }]);
        } else {
            this.setSelection([...(this.control.value ?? []), { type: 'custom', value: trimmed }]);
        }
        if (this.singleSelection()) this.closePanel();
    }

    protected removeCustom(value: string): void {
        this.setSelection((this.control.value ?? []).filter(item => item.type !== 'custom' || item.value !== value));
    }

    protected toggleCustomItem(value: string, checked: boolean): void {
        if (checked) {
            this.uncheckCustom(value);
        } else {
            const idx = this.uncheckedCustomValues().indexOf(value);
            if (idx !== -1) this.recheckCustom(idx);
        }
    }

    protected removeCustomItem(value: string, checked: boolean): void {
        const posInList = this.allCustomItems().findIndex(i => i.value === value);
        if (checked) {
            this.removeCustom(value);
        } else {
            const idx = this.uncheckedCustomValues().indexOf(value);
            if (idx !== -1) this.removeUnchecked(idx);
        }
        afterNextRender(
            () => {
                const opts = Array.from(document.querySelectorAll<HTMLElement>('[data-option-btn]:not([disabled])'));
                (opts[posInList] ?? opts[posInList - 1] ?? opts[0])?.focus();
            },
            { injector: this.injector }
        );
    }

    protected uncheckCustom(value: string): void {
        this.removeCustom(value);
        this.uncheckedCustomValues.update(values => [...values, value]);
    }

    protected recheckCustom(index: number): void {
        if (!this.singleSelection() && !this.canSelectMore()) return;
        const value = this.uncheckedCustomValues()[index];
        this.uncheckedCustomValues.update(values => values.filter((_, i) => i !== index));
        if (this.singleSelection()) {
            this.setSelection([{ type: 'custom', value }]);
        } else {
            this.setSelection([...(this.control.value ?? []), { type: 'custom', value }]);
        }
        if (this.singleSelection()) this.closePanel();
    }

    protected isCustomItemDisabled(checked: boolean): boolean {
        return (
            this.control.disabled ||
            this.readonly() ||
            (!this.singleSelection() && this.maxSelectionReached() && !checked)
        );
    }

    protected isAddCustomDisabled(): boolean {
        return this.control.disabled || this.readonly() || (!this.singleSelection() && this.maxSelectionReached());
    }

    protected removeUnchecked(index: number): void {
        this.uncheckedCustomValues.update(values => values.filter((_, i) => i !== index));
    }

    @HostListener('document:mousedown', ['$event'])
    protected onDocumentMouseDown(event: MouseEvent): void {
        if (!this.open()) return;
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (!this.hostElement.nativeElement.contains(target)) {
            this.open.set(false);
        }
    }

    private scheduleOverflowUpdate(): void {
        afterNextRender(() => this.computeOverflow(), { injector: this.injector });
    }

    private updateInvalidState(): void {
        this.isInvalid.set(this.control.invalid && this.control.touched);
    }

    private computeOverflow(): void {
        const container = this.namesContainerRef()?.nativeElement;
        const badge = this.overflowBadgeRef()?.nativeElement;
        if (!container) {
            this.overflowCount.set(0);
            return;
        }

        const items = Array.from(container.querySelectorAll<HTMLElement>('[data-name-item]'));
        if (!items.length) {
            this.overflowCount.set(0);
            return;
        }

        items.forEach(el => el.classList.remove('invisible', 'absolute'));
        badge?.classList.remove('invisible');

        const containerWidth = container.offsetWidth;
        const badgeWidth = badge ? badge.offsetWidth : 0;

        let firstHiddenIndex = items.length;
        for (let i = 0; i < items.length; i++) {
            if (items[i].offsetLeft + items[i].offsetWidth > containerWidth) {
                firstHiddenIndex = i;
                break;
            }
        }

        if (firstHiddenIndex === items.length) {
            badge?.classList.add('invisible');
            this.overflowCount.set(0);
            return;
        }

        const effectiveWidth = containerWidth - badgeWidth;
        firstHiddenIndex = items.length;
        for (let i = 0; i < items.length; i++) {
            if (items[i].offsetLeft + items[i].offsetWidth > effectiveWidth) {
                firstHiddenIndex = i;
                break;
            }
        }

        for (let i = firstHiddenIndex; i < items.length; i++) {
            items[i].classList.add('invisible', 'absolute');
        }
        this.overflowCount.set(items.length - firstHiddenIndex);
    }

    private listenToControlValueChanges(): void {
        this.updateSelectionState(this.control.value);
        this.control.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(value => this.updateSelectionState(value))
            )
            .subscribe();
    }

    private updateSelectionState(selection: CustomValueSelectItem<T>[] | null): void {
        this.selectedIds.set(
            new Set(
                selection
                    ?.filter(item => item.type === 'predefined')
                    .map(item => String(item.value[this.identifierProperty()])) ?? []
            )
        );
        this.selectedCustomValues.set(selection?.filter(item => item.type === 'custom').map(item => item.value) ?? []);
    }

    private setSelection(selection: CustomValueSelectItem<T>[]): void {
        this.control.setValue(selection);
        this.control.markAsTouched();
        this.control.markAsDirty();
        this.onChange(selection);
        this.onTouch();
    }

    private selectedCount(): number {
        return this.selectedIds().size + this.selectedCustomValues().length;
    }

    private canSelectMore(): boolean {
        const max = this.normalizedMaxSelected();
        return max === null || this.selectedCount() < max;
    }

    private normalizedMaxSelected(): number | null {
        const raw = this.maxSelected();
        if (raw === null || raw === undefined || raw === '') return null;
        const parsed = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(parsed) || parsed < 1) return null;
        return Math.floor(parsed);
    }
}
